import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type {
  Attachment,
  AvailableTransition,
  CompletionRecord,
  Investigation,
  PreApprovalStatus,
  ProposalResponse,
  RepairSchedule,
  WorkOrderDetail,
} from '../api/types.js';
import { Alert, Button, Card, classes } from '../components/ui.js';
import {
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_MEANINGS,
  type WorkOrderStatus,
  workOrderStatusLabel,
} from '../../shared/workflow.js';

export type NextStepTab =
  'overview' | 'investigation' | 'proposals' | 'execution' | 'documents' | 'timeline';

export interface Step {
  label: string;
  hint?: string;
  done: boolean;
  tab?: NextStepTab;
  optional?: boolean;
  /** A step that is one of the top-right status buttons; the strip can press it. */
  transition?: WorkOrderStatus;
}

/**
 * Tells VGS what the work order still needs before it can move on, so a facility
 * manager is guided to the next action instead of discovering it from a rejection.
 */
export function NextStepsPanel({
  workOrder,
  availableTransitions = [],
  onNavigate,
}: {
  workOrder: WorkOrderDetail;
  availableTransitions?: AvailableTransition[];
  onNavigate: (tab: NextStepTab) => void;
}) {
  const id = workOrder.id;
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const transition = useMutation({
    mutationFn: (toStatus: WorkOrderStatus) =>
      apiRequest(`/api/work-orders/${id}/transition`, { method: 'POST', body: { toStatus } }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['work-order', id] });
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline', id] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'That step could not be recorded. Please try again.',
      );
    },
  });
  const proposals = useQuery({
    queryKey: ['proposals', id],
    queryFn: () => apiRequest<ProposalResponse>(`/api/work-orders/${id}/proposals`),
  });
  const attachments = useQuery({
    queryKey: ['attachments', id],
    queryFn: () =>
      apiRequest<{ attachments: Attachment[] }>(`/api/work-orders/${id}/attachments`).then(
        (result) => result.attachments,
      ),
  });
  const investigation = useQuery({
    queryKey: ['investigation', id],
    queryFn: () =>
      apiRequest<{ investigation: Investigation | null }>(
        `/api/work-orders/${id}/investigation`,
      ).then((result) => result.investigation),
  });
  const schedule = useQuery({
    queryKey: ['schedule', id],
    queryFn: () =>
      apiRequest<{ schedule: RepairSchedule | null }>(`/api/work-orders/${id}/schedule`).then(
        (result) => result.schedule,
      ),
  });
  const completion = useQuery({
    queryKey: ['completion', id],
    queryFn: () =>
      apiRequest<{ completion: CompletionRecord | null }>(`/api/work-orders/${id}/completion`).then(
        (result) => result.completion,
      ),
  });
  const preApproval = useQuery({
    queryKey: ['pre-approval', id],
    queryFn: () => apiRequest<PreApprovalStatus>(`/api/work-orders/${id}/pre-approval`),
  });

  const plan = buildPlan({
    workOrder,
    proposals: proposals.data,
    attachments: attachments.data,
    investigation: investigation.data,
    schedule: schedule.data,
    completion: completion.data,
    preApproval: preApproval.data,
  });
  // Closed, cancelled and rejected work orders have no next step, but the strip
  // still shows how far they got.
  if (!plan) {
    return (
      <div className="flex flex-col gap-3">
        <WorkflowStrip
          status={workOrder.status}
          statusBeforeHold={workOrder.statusBeforeHold}
          supplies={workOrder.kind === 'supplies'}
        />
        <Card className="border-brand-200 bg-brand-50/30">
          <p className="text-sm text-slate-800">
            <span className="font-semibold">
              {workOrderStatusLabel(workOrder.status, workOrder.kind)}
            </span>
            {' — '}
            {statusMeaning(workOrder)}
          </p>
        </Card>
      </div>
    );
  }

  const next = plan.steps.find((step) => !step.done && !step.optional) ?? null;
  const nextIsTransition =
    next?.transition !== undefined &&
    availableTransitions.some((item) => item.toStatus === next.transition);
  const remaining = plan.steps.filter((step) => !step.done && !step.optional).length;

  return (
    <div className="flex flex-col gap-3">
      <WorkflowStrip
        status={workOrder.status}
        statusBeforeHold={workOrder.statusBeforeHold}
        supplies={workOrder.kind === 'supplies'}
      />
      <Card className="border-brand-200 bg-brand-50/30">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Where this work order is
            </p>
            <p className="text-sm text-slate-800">
              <span className="font-semibold">
                {workOrderStatusLabel(workOrder.status, workOrder.kind)}
              </span>
              {' — '}
              {statusMeaning(workOrder)}
            </p>
            {next ? (
              <p className="mt-2 text-sm text-slate-800">
                <span className="font-semibold">Your next step:</span> {next.label}
                {next.hint && <span className="block text-xs text-slate-600">{next.hint}</span>}
              </p>
            ) : (
              plan.ready && <p className="mt-2 text-sm font-medium text-green-700">{plan.ready}</p>
            )}
            {error && (
              <div className="mt-2">
                <Alert variant="error">{error}</Alert>
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            {next && (nextIsTransition || next.tab) && (
              <Button
                loading={transition.isPending}
                onClick={() => {
                  if (nextIsTransition && next.transition) transition.mutate(next.transition);
                  else if (next.tab) onNavigate(next.tab);
                }}
              >
                {nextIsTransition
                  ? actionWord(next.transition as WorkOrderStatus, workOrder.kind)
                  : next.label}
              </Button>
            )}
            {plan.steps.length > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-brand-700 underline"
                onClick={() => setShowAll((value) => !value)}
              >
                {showAll
                  ? 'Hide the full checklist'
                  : `Show the full checklist (${remaining} to do)`}
              </button>
            )}
          </div>
        </div>
      </Card>
      {showAll && <StepsCard title="Next steps" plan={plan} onNavigate={onNavigate} />}
    </div>
  );
}

function actionWord(status: WorkOrderStatus, kind?: 'repair' | 'supplies'): string {
  switch (status) {
    case 'under_vgs_review':
      return 'Acknowledge';
    case 'estimate_in_progress':
      return 'Begin estimate';
    case 'work_in_progress':
      return 'Start work';
    case 'closed':
      return 'Close work order';
    default:
      return `Move to ${workOrderStatusLabel(status, kind)}`;
  }
}

const MILESTONES: { label: string; statuses: WorkOrderStatus[] }[] = [
  { label: 'Entered', statuses: ['submitted'] },
  { label: 'Acknowledged', statuses: ['under_vgs_review'] },
  { label: 'Quote', statuses: ['estimate_in_progress', 'revision_requested'] },
  { label: 'With USPS', statuses: ['awaiting_customer_approval'] },
  { label: 'Approved', statuses: ['approved'] },
  { label: 'Procurement', statuses: ['procurement'] },
  { label: 'Scheduled', statuses: ['repair_scheduled'] },
  { label: 'In progress', statuses: ['work_in_progress'] },
  { label: 'Completed', statuses: ['completed'] },
  { label: 'Closed', statuses: ['closed'] },
];

/** The whole journey in one line, so a facility manager can see how far along it is. */
export function WorkflowStrip({
  status,
  statusBeforeHold,
  supplies = false,
}: {
  status: WorkOrderStatus;
  statusBeforeHold?: WorkOrderStatus | null;
  /** Supplies orders are received, not scheduled and worked. */
  supplies?: boolean;
}) {
  const effective = status === 'on_hold' && statusBeforeHold ? statusBeforeHold : status;
  const milestones = supplies
    ? MILESTONES.filter(
        (item) =>
          !item.statuses.includes('work_in_progress') && !item.statuses.includes('procurement'),
      ).map((item) =>
        item.statuses.includes('repair_scheduled')
          ? {
              label: 'Ordered',
              statuses: ['repair_scheduled', 'work_in_progress'] as WorkOrderStatus[],
            }
          : item.statuses.includes('completed')
            ? { label: 'Received', statuses: item.statuses }
            : item,
      )
    : MILESTONES;
  const current = milestones.findIndex((item) => item.statuses.includes(effective));
  const stopped = ['cancelled', 'rejected'].includes(status);
  return (
    <ol className="flex flex-wrap items-center gap-y-2 text-xs" aria-label="Workflow progress">
      {milestones.map((item, index) => {
        const done = current > index || status === 'closed';
        const active = current === index && status !== 'closed';
        return (
          <li key={item.label} className="flex items-center">
            <span
              className={classes(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ring-1 ring-inset',
                done && 'bg-green-50 text-green-800 ring-green-300',
                active && !stopped && 'bg-brand-700 text-white ring-brand-700',
                active && stopped && 'bg-red-50 text-red-800 ring-red-300',
                !done && !active && 'bg-white text-slate-500 ring-slate-200',
              )}
              aria-current={active ? 'step' : undefined}
            >
              {done ? '✓ ' : `${index + 1}. `}
              {item.label}
              {active && status === 'on_hold' && ' (on hold)'}
              {active && stopped && ` (${WORK_ORDER_STATUS_LABELS[status].toLowerCase()})`}
            </span>
            {index < milestones.length - 1 && (
              <span aria-hidden="true" className="mx-1 text-slate-300">
                ›
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export interface StepPlan {
  stage: string;
  intro?: string;
  steps: Step[];
  ready?: string;
}

export function StepsCard({
  title,
  plan,
  onNavigate,
}: {
  title: string;
  plan: StepPlan;
  onNavigate: (tab: NextStepTab) => void;
}) {
  const remaining = plan.steps.filter((step) => !step.done && !step.optional).length;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <span className="text-base font-semibold text-slate-900">{title}</span>
          <span className="rounded bg-brand-50 px-1.5 py-0.5 text-xs font-semibold text-brand-800">
            {plan.stage}
          </span>
        </span>
      }
      className="border-brand-200 bg-brand-50/30"
    >
      {plan.intro && <p className="mb-3 text-sm text-slate-700">{plan.intro}</p>}
      {plan.steps.length > 0 && (
        <ol className="flex flex-col gap-2">
          {plan.steps.map((step) => (
            <li key={step.label} className="flex items-start gap-2 text-sm">
              <span
                aria-hidden="true"
                className={
                  step.done
                    ? 'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-green-600 text-[10px] font-bold text-white'
                    : 'mt-0.5 inline-flex h-4 w-4 shrink-0 rounded-full border-2 border-slate-400'
                }
              >
                {step.done ? '✓' : ''}
              </span>
              <span className={step.done ? 'text-slate-500 line-through' : 'text-slate-900'}>
                {step.tab && !step.done ? (
                  <button
                    type="button"
                    className="font-medium text-brand-700 underline"
                    onClick={() => onNavigate(step.tab as NextStepTab)}
                  >
                    {step.label}
                  </button>
                ) : (
                  <span className="font-medium">{step.label}</span>
                )}
                {step.optional && !step.done && (
                  <span className="ml-1 text-xs text-slate-500">(optional)</span>
                )}
                {step.hint && !step.done && (
                  <span className="block text-xs text-slate-600">{step.hint}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
      {plan.steps.length > 0 && remaining === 0 && plan.ready && (
        <p className="mt-3 text-sm font-medium text-green-700">{plan.ready}</p>
      )}
    </Card>
  );
}

const SUPPLIES_MEANINGS: Partial<Record<WorkOrderStatus, string>> = {
  procurement:
    'USPS approved — the PR/PO (or P-card) is being arranged before the items are ordered.',
  approved:
    'USPS approved the price — place the order on the payment method chosen, then record the receipt when the items arrive.',
  repair_scheduled: 'The order has been placed — record the receipt when the items arrive.',
  work_in_progress: 'The items have been ordered — record the receipt when they arrive.',
  completed: 'The items were received; close the work order once the invoice is recorded.',
};

/** Plain-language status meaning, worded for supplies orders where it differs. */
function statusMeaning(workOrder: WorkOrderDetail): string {
  return (
    (workOrder.kind === 'supplies' ? SUPPLIES_MEANINGS[workOrder.status] : undefined) ??
    WORK_ORDER_STATUS_MEANINGS[workOrder.status]
  );
}

function buildPlan({
  workOrder,
  proposals,
  attachments,
  investigation,
  schedule,
  completion,
  preApproval,
}: {
  workOrder: WorkOrderDetail;
  proposals?: ProposalResponse;
  attachments?: Attachment[];
  investigation?: Investigation | null;
  schedule?: RepairSchedule | null;
  completion?: CompletionRecord | null;
  preApproval?: PreApprovalStatus;
}): StepPlan | null {
  const supplies = workOrder.kind === 'supplies';
  const versions = proposals?.versions ?? [];
  const estimates = versions.filter((version) => version.kind === 'estimate');
  const finalInvoices = versions.filter((version) => version.kind === 'final_invoice');
  const draft = versions.find((version) => version.status === 'draft');
  const approvedInvoice = finalInvoices.some((version) => version.status === 'approved');
  const pendingInvoice = finalInvoices.some((version) =>
    ['draft', 'submitted'].includes(version.status),
  );
  const quoteCount = proposals?.subcontractorQuotes.length ?? 0;
  const invoiceDocs = (attachments ?? []).filter((item) => item.category === 'invoice').length;

  const authorizedToProceed = versions.some(
    (version) => version.kind === 'proceed_authorization' && version.status === 'approved',
  );
  const urgentWork =
    workOrder.emergencyCallout || ['emergency', 'urgent'].includes(workOrder.priority);

  const emergencyHint: Step | null =
    !workOrder.emergencyCallout &&
    ['submitted', 'under_vgs_review', 'estimate_in_progress'].includes(workOrder.status)
      ? {
          label: 'Was the work already performed as an emergency call-out?',
          hint: 'If a subcontractor was called out before a price could be approved, mark it on the Overview tab (Emergency call-out card) so USPS sees it and the three-quote rule is waived.',
          done: false,
          tab: 'overview',
          optional: true,
        }
      : null;

  switch (workOrder.status) {
    case 'submitted':
      return {
        stage: 'New request',
        intro: 'USPS has submitted this request. VGS must acknowledge it to start the clock.',
        steps: [
          {
            label: 'Acknowledge the request',
            hint: 'Use the Acknowledge button at the top right of this page.',
            done: false,
            transition: 'under_vgs_review',
          },
          ...(emergencyHint ? [emergencyHint] : []),
        ],
      };

    case 'under_vgs_review':
      return {
        stage: 'Under VGS review',
        intro: 'Look at the problem, then begin the estimate.',
        steps: [
          {
            label: 'Record the site investigation',
            hint: 'Inspection date, findings and recommended action. USPS can see this.',
            done: Boolean(investigation),
            tab: 'investigation',
            optional: true,
          },
          ...(emergencyHint ? [emergencyHint] : []),
          {
            label: 'Begin the estimate',
            hint: 'Use the "Begin estimate" button at the top right of this page.',
            done: false,
            transition: 'estimate_in_progress',
          },
        ],
      };

    case 'estimate_in_progress':
    case 'revision_requested': {
      const current = draft;
      const hasCurrent = Boolean(current);
      const visibleItems = (current?.costItems ?? []).filter(
        (item) => item.visibility !== 'internal',
      );
      const hasSubcontract = visibleItems.some((item) => item.costCategory === 'subcontractor');
      const steps: Step[] = [
        {
          label:
            workOrder.status === 'revision_requested'
              ? 'Prepare the revised proposal'
              : estimates.length > 0
                ? 'Prepare the corrected proposal'
                : 'Prepare the proposal',
          hint: urgentWork
            ? 'Proposal tab → Request authorization to proceed if there is no quote yet (describe the problem and who at USPS called it in, with a not-to-exceed amount of at most $2,500). Otherwise Prepare proposal with the price.'
            : 'Proposal tab → Prepare proposal. Enter the subcontractor or labor price; the markup is added automatically.',
          done: hasCurrent,
          tab: 'proposals',
        },
        {
          label: 'Enter the scope of work',
          hint: 'On the draft, click Edit draft and describe the work in the Scope of work box.',
          done: Boolean(current?.scopeOfWork?.trim()),
          tab: 'proposals',
        },
        {
          label: 'Add at least one cost line visible to USPS',
          hint: 'Lines marked VGS internal are not shown to USPS and do not count.',
          done: visibleItems.length > 0 || current?.kind === 'proceed_authorization',
          tab: 'proposals',
        },
      ];
      if (hasSubcontract || (!hasCurrent && quoteCount === 0)) {
        steps.push({
          label: 'Attach the subcontractor quote or invoice, visible to USPS',
          hint: 'Documents & comments tab → upload with category "Subcontractor quote" and visibility USPS-visible. Any other category or "VGS internal" does not satisfy this.',
          done: quoteCount > 0,
          tab: 'documents',
          optional: !hasSubcontract,
        });
      }
      if (preApproval?.referralRequired) {
        const determination = preApproval.determination;
        steps.push({
          label: 'Record the USPS Facilities Response Line call',
          hint: 'Above the referral threshold the repair must be called in first; record the ticket number and outcome on the Proposal tab.',
          done: Boolean(determination && determination.outcome !== 'pending'),
          tab: 'proposals',
        });
      }
      if (preApproval?.competitionRequired) {
        steps.push({
          label: `Obtain ${preApproval.quotesRequired} subcontractor quotes (${preApproval.quotesObtained} attached)`,
          hint: 'Or request a competition exception from the Contracting Officer on the Proposal tab.',
          done: preApproval.quotesObtained >= preApproval.quotesRequired,
          tab: 'proposals',
        });
      }
      steps.push({
        label: 'Submit to USPS',
        hint: 'The COR is emailed when you submit; nothing is sent before that.',
        done: false,
        tab: 'proposals',
      });
      if (emergencyHint) steps.splice(1, 0, emergencyHint);
      return {
        stage: workOrder.status === 'revision_requested' ? 'Revision requested' : 'Pricing',
        intro:
          workOrder.status === 'revision_requested'
            ? 'USPS asked for changes. Prepare a revised proposal and resubmit.'
            : undefined,
        steps,
      };
    }

    case 'awaiting_customer_approval': {
      const submitted = versions.find((version) => version.status === 'submitted');
      const invoiceReview = submitted?.kind === 'final_invoice';
      return {
        stage: invoiceReview ? 'Invoice with USPS' : 'With USPS',
        intro: submitted?.awaitingCorReview
          ? 'The COR must endorse this before the Contracting Officer gives final approval. No action needed from VGS.'
          : `USPS (${submitted?.requiredAuthorityLabel ?? 'the approver'}) has been emailed and must approve. No action needed from VGS.`,
        steps: [
          {
            label: 'If the amount is wrong, use "Withdraw and correct" on the Proposal tab',
            done: false,
            tab: 'proposals',
            optional: true,
          },
        ],
      };
    }

    case 'procurement': {
      const procured = Boolean(workOrder.procurement?.completedAt);
      if (supplies) {
        return {
          stage: 'Procurement Stage',
          intro: procured
            ? 'The PR/PO (or P-card) has been recorded and the items can be ordered. When they arrive, record the receipt.'
            : 'USPS approved the price. An administrator must record the Purchase Requisition and Purchase Order numbers (or tick Paid by P-card) before the items are ordered; the facility is emailed when that is done.',
          steps: [
            {
              label: 'Administrator records the PR and PO numbers, or Paid by P-card',
              hint: 'Receipt & completion tab → Procurement → Record procurement. Administrators and the program manager only.',
              done: procured,
              tab: 'execution',
            },
            {
              label: 'Items received — record the receipt',
              hint: 'Receipt & completion tab → Completion documentation → enter the date the items arrived, tick "Mark the items as received" (and "Also close the work order now" if the invoice is in hand).',
              done: Boolean(completion),
              tab: 'execution',
            },
          ],
        };
      }
      const urgentWork =
        workOrder.emergencyCallout || ['emergency', 'urgent'].includes(workOrder.priority);
      return {
        stage: 'Procurement Stage',
        intro: procured
          ? 'The PR/PO (or P-card) has been recorded. Schedule the repair — or, if the work is already done, record the completion directly.'
          : urgentWork
            ? 'USPS approved the price. An administrator records the PR/PO (or P-card) under Procurement; because this is emergency/urgent work, scheduling and completion are not held up by it.'
            : 'USPS approved the price. An administrator must record the Purchase Requisition and Purchase Order numbers (or tick Paid by P-card) before the repair can be scheduled; the facility is emailed when that is done.',
        steps: [
          {
            label: 'Administrator records the PR and PO numbers, or Paid by P-card',
            hint: 'Schedule & completion tab → Procurement → Record procurement. Administrators and the program manager only.',
            done: procured,
            tab: 'execution',
          },
          {
            label: 'Schedule the repair',
            hint: 'Schedule & completion tab → Schedule repair → enter the date and window → Save schedule. USPS is notified.',
            done: Boolean(schedule),
            tab: 'execution',
          },
          {
            label: 'Already done? Record completion straight away',
            hint: 'Schedule & completion tab → Completion documentation → enter the date the work was actually finished, tick "Mark completed". The schedule and start are filled in for you.',
            done: Boolean(completion),
            tab: 'execution',
            optional: true,
          },
        ],
      };
    }

    case 'approved':
      if (supplies) {
        return {
          stage: 'Approved',
          intro:
            'USPS approved the price. Place the order (no PR/PO — the payment method was chosen when the order was entered), then record the receipt when the items arrive.',
          steps: [
            {
              label: 'Order placed — record the order',
              hint: 'Receipt & completion tab → Order → enter the order/confirmation number, click "Order placed".',
              done: Boolean(workOrder.orderedAt),
              tab: 'execution',
            },
            {
              label: 'Items received — record the receipt',
              hint: 'Receipt & completion tab → Completion documentation → enter the date the items arrived, tick "Mark the items as received".',
              done: Boolean(completion),
              tab: 'execution',
            },
          ],
        };
      }
      return {
        stage: 'Approved',
        intro:
          'USPS approved the price. Schedule the repair — or, if the work is already done, record the completion directly.',
        steps: [
          {
            label: 'Schedule the repair',
            hint: 'Schedule & completion tab → enter the date and window. USPS is notified.',
            done: Boolean(schedule),
            tab: 'execution',
          },
          {
            label: 'Already done? Record completion straight away',
            hint: 'Schedule & completion tab → Completion documentation → enter the date the work was actually finished, tick "Mark completed". The schedule and start are filled in for you.',
            done: Boolean(completion),
            tab: 'execution',
            optional: true,
          },
        ],
      };

    case 'repair_scheduled':
      if (supplies) {
        return {
          stage: 'Ordered',
          intro: 'The order has been placed. When the items arrive, record the receipt.',
          steps: [
            {
              label: 'Items received — record the receipt',
              hint: 'Receipt & completion tab → Completion documentation → enter the date the items arrived, tick "Mark the items as received" (and "Also close" if the receipt is attached).',
              done: Boolean(completion),
              tab: 'execution',
            },
          ],
        };
      }
      return {
        stage: 'Scheduled',
        steps: [
          {
            label: 'Start work when the crew is on site',
            hint: 'Use the "Start work" button at the top right of this page.',
            done: false,
            transition: 'work_in_progress',
          },
          {
            label: 'Already done? Record completion straight away',
            hint: 'Schedule & completion tab → Completion documentation → enter the date the work was actually finished, tick "Mark completed".',
            done: Boolean(completion),
            tab: 'execution',
            optional: true,
          },
        ],
      };

    case 'work_in_progress':
      return {
        stage: 'In progress',
        steps: [
          {
            label: 'Record completion and mark the work complete',
            hint: 'Schedule & completion tab → work performed, final notes, then "Mark completed".',
            done: Boolean(completion),
            tab: 'execution',
          },
        ],
      };

    case 'completed': {
      const steps: Step[] = [];
      if (authorizedToProceed || finalInvoices.length > 0) {
        steps.push({
          label: pendingInvoice
            ? 'Final invoice is with USPS for approval'
            : 'Submit the actual invoice amount for USPS approval',
          hint: 'Work authorized against a not-to-exceed amount must have the actual price approved before closing. Proposal tab → Submit final invoice.',
          done: approvedInvoice,
          tab: 'proposals',
        });
      }
      steps.push(
        {
          label: 'Attach the subcontractor invoice',
          hint: 'Documents & comments tab → category "Invoice".',
          done: invoiceDocs > 0,
          tab: 'documents',
          optional: true,
        },
        {
          label: 'Enter the VGS invoice number',
          hint: 'Overview tab → Invoice card. Reported to USPS.',
          done: Boolean(workOrder.invoiceNumber),
          tab: 'overview',
          optional: true,
        },
        {
          label: 'Close the work order',
          hint: 'Use the "Close work order" button at the top right of this page.',
          done: false,
          transition: 'closed',
        },
      );
      return { stage: 'Completed', steps };
    }

    case 'on_hold':
      return {
        stage: 'On hold',
        steps: [
          {
            label: 'Resume when ready',
            hint: 'Use the Resume button at the top right of this page.',
            done: false,
          },
        ],
      };

    default:
      return null;
  }
}
